import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronRight as ChevronRightIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { ResourceName, type ClaimItem } from "@/types";
import { StyledTab, StyledTabs } from "@/components/ui/mui/StyledTabs";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getResourceIcon } from "@/utils/icons";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { relationshipLabel } from "../document-review/claimantGridCells";
import {
  filterCounts,
  filterRows,
  footerFor,
  isOutsidePolicy,
  outcomeDisplay,
  outcomeNote,
  type ClaimantRow,
  type ClaimantRowFilter,
} from "./loadResultModel";

export const CLAIMANTS_PER_PAGE = 25;

const FILTERS: Array<{ key: ClaimantRowFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "outside-policy", label: "Outside policy" },
  { key: "stop-loss", label: "Stop-Loss claims" },
  { key: "attention", label: "Needs attention" },
];

const StoplossClaimIcon = getResourceIcon(ResourceName.STOPLOSS_CLAIMS);

const money = (n: number) => formatUSD(n);
const wholeDollars = (n: number) =>
  Number.isInteger(n) ? formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : formatUSD(n);
const date = (v: string | null | undefined) => (v ? formatDate(String(v), DATE_FORMATS.DISPLAY_LONG) : "—");

interface LoadClaimantsTableProps {
  rows: ClaimantRow[];
  /** Line fetch hit the page ceiling: counts show "N+" and the footer is marked. */
  linesPartial?: boolean;
  /** One quiet line above the filters (the load's warnings, summarised); no banner. */
  notice?: React.ReactNode;
}

/**
 * The review Claimants grid, post-load (#7550 AC2–AC7, AC12): Claimant ·
 * Paid in file · YTD · Deductible · Outcome · open, expanding to the
 * claimant's lines. Filters pivot the same rows; the footer totals Paid in
 * file over the filtered rows (every page) and nothing else: YTD is a running
 * total and is never summed.
 */
export const LoadClaimantsTable = ({ rows, linesPartial = false, notice }: LoadClaimantsTableProps) => {
  const [filter, setFilter] = useState<ClaimantRowFilter>("all");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { theme } = useAppTheme();
  // The review's On approve pill: navy, 24px, 12/600 (ForecastChip).
  const navy = theme.palette.secondary.main;

  const counts = useMemo(() => filterCounts(rows), [rows]);
  const filtered = useMemo(() => filterRows(rows, filter), [rows, filter]);
  const footer = useMemo(() => footerFor(filtered), [filtered]);
  const showPagination = filtered.length > CLAIMANTS_PER_PAGE;
  const pageRows = showPagination
    ? filtered.slice(page * CLAIMANTS_PER_PAGE, (page + 1) * CLAIMANTS_PER_PAGE)
    : filtered;

  useEffect(() => setPage(0), [filter, rows.length]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Box data-testid="load-claimants-table" sx={{ px: 1.5 }}>
      {notice && <Box sx={{ px: 0.5, pb: 1.5, "&:empty": { display: "none" } }}>{notice}</Box>}
      {/* Same pill tabs the review uses for its views. */}
      <StyledTabs
        value={filter}
        onChange={(_e, v: ClaimantRowFilter) => setFilter(v)}
        sx={{ px: 0.5, pb: 1.5, minHeight: 0 }}
        aria-label="Filter claimants"
      >
        {FILTERS.filter((f) => f.key === "all" || counts[f.key] > 0).map((f) => (
          <StyledTab
            key={f.key}
            value={f.key}
            label={`${f.label} ${counts[f.key]}`}
            sx={{ padding: "4px 12px", minHeight: 28, fontSize: 13 }}
          />
        ))}
      </StyledTabs>

      <TableContainer>
        <Table size="small" aria-label="Claimants loaded from this file">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Claimant</TableCell>
              <TableCell align="right">Paid in file</TableCell>
              <TableCell align="right">YTD</TableCell>
              <TableCell>Deductible</TableCell>
              <TableCell>Outcome</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row) => {
              const isOpen = expanded.has(row.claimantId);
              const display = outcomeDisplay(row);
              const note = outcomeNote(row, money);
              const rel = relationshipLabel(row.relationship);
              const lineCount = `${row.lines.length}${row.linesPartial ? "+" : ""} ${row.lines.length === 1 && !row.linesPartial ? "line" : "lines"}`;
              return (
                <Fragment key={row.claimantId}>
                  <TableRow hover data-testid={`claimant-row-${row.claimantId}`}>
                    <TableCell padding="none" sx={{ pl: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => toggle(row.claimantId)}
                        aria-label={`${isOpen ? "Hide" : "Show"} claim lines for ${row.name}`}
                        aria-expanded={isOpen}
                        disabled={row.lines.length === 0}
                      >
                        {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ minWidth: 0 }}>
                      {row.claimant ? (
                        <Link
                          component={RouterLink}
                          to={`/${ResourceName.CLAIMANTS}/${row.claimantId}/show`}
                          variant="body2"
                          underline="hover"
                          noWrap
                          title={row.memberId ? `Member ID ${row.memberId}` : undefined}
                          sx={{ fontWeight: 500, display: "block" }}
                        >
                          {row.name}
                        </Link>
                      ) : (
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {row.name}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ lineHeight: 1.3, display: "block" }}
                      >
                        {[rel, lineCount].filter(Boolean).join(" · ")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 500 }}>
                      {money(row.paidInFile)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {row.ytdAfter !== undefined ? (
                        <>
                          <Typography variant="body2">{money(row.ytdAfter)}</Typography>
                          {row.ytdBefore !== undefined && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ lineHeight: 1.3, display: "block" }}
                            >
                              was {money(row.ytdBefore)}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.deductible !== undefined ? (
                        <>
                          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {wholeDollars(row.deductible)}
                          </Typography>
                          {row.deductibleLabel && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ lineHeight: 1.3, display: "block" }}
                            >
                              {row.deductibleLabel}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Tooltip title="No specific deductible or laser applies on this policy." arrow>
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {display.kind === "pill" ? (
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            height: 24,
                            px: 1,
                            borderRadius: "12px",
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            bgcolor: display.filled ? navy : "transparent",
                            color: display.filled ? theme.palette.getContrastText(navy) : navy,
                            border: display.filled ? "none" : `1px solid ${navy}`,
                          }}
                        >
                          <StoplossClaimIcon sx={{ fontSize: 13, flexShrink: 0 }} aria-hidden />
                          {display.label}
                        </Box>
                      ) : (
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            fontWeight: display.bold ? 600 : 400,
                            color: display.bold ? "text.primary" : "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {display.label}
                        </Typography>
                      )}
                      {note && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.25,
                            lineHeight: 1.3,
                            color: row.attention ? "warning.dark" : "text.secondary",
                          }}
                          title={row.attention?.detail}
                        >
                          {note}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, borderBottom: isOpen ? undefined : 0 }}>
                      <Collapse in={isOpen} unmountOnExit>
                        <LoadClaimLinesPanel row={row} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
            {pageRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    No claimants match this filter
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 600, color: "text.primary", fontSize: "0.8125rem" }}>
                {footer.claimants} {footer.claimants === 1 ? "claimant" : "claimants"} · {footer.lines}
                {linesPartial ? "+" : ""} {footer.lines === 1 && !linesPartial ? "line" : "lines"}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: "0.8125rem",
                  fontVariantNumeric: "tabular-nums",
                }}
                data-testid="paid-in-file-total"
              >
                {money(footer.paidInFile)}
              </TableCell>
              <TableCell colSpan={3} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      {linesPartial && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.5, pt: 1 }}>
          Some claimants have more history than one request returns, so Paid in file may be short for them.
        </Typography>
      )}
      {showPagination && (
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={CLAIMANTS_PER_PAGE}
          rowsPerPageOptions={[CLAIMANTS_PER_PAGE]}
        />
      )}
    </Box>
  );
};

const LINES_GRID = "minmax(96px, 120px) minmax(96px, 120px) minmax(160px, 1fr) 110px 110px 72px";

/**
 * The review's lines panel shape (ClaimLinesPanel): facts band, then
 * Service date · Paid date · Procedure · Paid · Status, with the review's
 * Source column replaced by a Record link (#7550 AC6).
 */
export const LoadClaimLinesPanel = ({ row }: { row: ClaimantRow }) => {
  const facts: Array<{ label: string; value: string }> = [];
  if (row.memberId) facts.push({ label: "Member ID", value: row.memberId });
  const rel = relationshipLabel(row.relationship);
  if (rel) facts.push({ label: "Relationship", value: rel });
  if (row.birthDate) facts.push({ label: "Born", value: date(row.birthDate) });

  const cell = { fontSize: 13, whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" };
  const head = { ...cell, fontWeight: 600, color: "text.secondary" };

  return (
    <Box sx={{ px: 2, pt: 1, pb: 2, pl: 7, bgcolor: "action.hover" }} data-testid={`claim-lines-${row.claimantId}`}>
      {facts.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            columnGap: 3,
            rowGap: 0.5,
            pb: 1.25,
            mb: 1.25,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {facts.map((f) => (
            <Box key={f.label} sx={{ display: "flex", alignItems: "baseline", gap: 0.75, whiteSpace: "nowrap" }}>
              <Typography component="span" variant="caption" color="text.secondary">
                {f.label}
              </Typography>
              <Typography component="span" variant="body2">
                {f.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <Box
        role="table"
        aria-label={`Claim lines for ${row.name}`}
        sx={{ display: "grid", gridTemplateColumns: LINES_GRID, columnGap: 2 }}
      >
        <Box role="row" sx={{ display: "contents" }}>
          <Typography role="columnheader" sx={head}>
            Service date
          </Typography>
          <Typography role="columnheader" sx={head}>
            Paid date
          </Typography>
          <Typography role="columnheader" sx={head}>
            Procedure
          </Typography>
          <Typography role="columnheader" sx={{ ...head, textAlign: "right" }}>
            Paid
          </Typography>
          <Typography role="columnheader" sx={head} aria-label="Status" />
          <Typography role="columnheader" sx={{ ...head, textAlign: "right" }}>
            Record
          </Typography>
        </Box>
        {row.lines.map((line: ClaimItem) => {
          const procedure = [line.procedure_code, line.procedure_description].filter(Boolean).join(" ") || "—";
          const outside = isOutsidePolicy(line);
          return (
            <Box
              role="row"
              key={String(line.id)}
              sx={{ display: "contents", "& > *": { py: 0.75, borderTop: 1, borderColor: "divider" } }}
            >
              <Typography role="cell" sx={cell}>
                {date(line.date_of_service_start ?? line.date_of_service)}
              </Typography>
              <Typography role="cell" sx={cell}>
                {date(line.paid_date)}
              </Typography>
              <Typography role="cell" sx={cell} title={procedure}>
                {procedure}
              </Typography>
              <Typography role="cell" sx={{ ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                {money(Number(line.paid_amount) || 0)}
              </Typography>
              <Box role="cell">
                {outside && (
                  <Chip
                    label="Outside policy"
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
              </Box>
              <Box role="cell" sx={{ textAlign: "right" }}>
                <Link
                  component={RouterLink}
                  to={`/${ResourceName.CLAIM_ITEMS}/${line.id}/show`}
                  variant="body2"
                  sx={{ fontSize: 13 }}
                >
                  Open
                </Link>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
